import type { Certification, Education, Experience, ProfileData, ProfilePaper } from '@/hooks/use-profile'
import type {
  PublicProfile,
  PublicProfileCertification,
  PublicProfileEducation,
  PublicProfileExperience,
  PublicProfilePaper,
} from '@/types/public-share'

function mapPublicExperience(item: PublicProfileExperience): Experience {
  return {
    id: item.id,
    title: item.title,
    company: item.company,
    description: item.description ?? undefined,
    start_date: item.start_date,
    end_date: item.end_date ?? undefined,
    skills: item.skills ?? [],
  }
}

function mapPublicPaper(item: PublicProfilePaper): ProfilePaper {
  return {
    id: item.id ?? undefined,
    title: item.title,
    authors: item.authors ?? undefined,
    venue: item.venue ?? undefined,
    year: item.year ?? undefined,
    url: item.url ?? undefined,
    doi: item.doi ?? undefined,
    description: item.description ?? undefined,
  }
}

function mapPublicEducation(item: PublicProfileEducation): Education {
  return {
    id: item.id ?? undefined,
    institution: item.institution,
    degree: item.degree,
    field_of_study: item.field_of_study ?? undefined,
    start_date: item.start_date ?? undefined,
    end_date: item.end_date ?? undefined,
    description: item.description ?? undefined,
    related_skills: item.skills ?? [],
  }
}

function mapPublicCertification(item: PublicProfileCertification): Certification {
  return {
    id: item.id ?? undefined,
    name: item.name,
    issuer: item.issuer,
    issued_at: item.issued_at ?? undefined,
    expires_at: item.expires_at ?? undefined,
    credential_id: item.credential_id ?? undefined,
    credential_url: item.credential_url ?? undefined,
    description: item.description ?? undefined,
    related_skills: item.related_skills ?? [],
  }
}

/** Map the public profile payload onto the shared read-only standing sections. */
export function publicProfileToStandingData(profile: PublicProfile): ProfileData {
  const candidate = profile.extensions?.candidate ?? {}
  return {
    full_name: profile.full_name ?? undefined,
    headline: profile.headline ?? undefined,
    bio: profile.bio ?? undefined,
    city: profile.city ?? undefined,
    country: profile.country ?? undefined,
    website: profile.website ?? undefined,
    linkedin_url: profile.linkedin_url ?? undefined,
    x_url: profile.x_url ?? undefined,
    instagram_url: profile.instagram_url ?? undefined,
    facebook_url: profile.facebook_url ?? undefined,
    threads_url: profile.threads_url ?? undefined,
    tiktok_url: profile.tiktok_url ?? undefined,
    youtube_url: profile.youtube_url ?? undefined,
    substack_url: profile.substack_url ?? undefined,
    github_url: profile.github_url ?? undefined,
    extra_social_order: profile.extra_social_order ?? undefined,
    avatar_url: profile.avatar_url,
    banner_url: profile.banner_url,
    public_slug: profile.slug,
    extensions: {
      candidate: {
        open_to_work: candidate.open_to_work ?? false,
        skills: candidate.skills ?? [],
        skill_summaries: candidate.skill_summaries ?? [],
        experiences: (candidate.experiences ?? []).map(mapPublicExperience),
        papers: (candidate.papers ?? []).map(mapPublicPaper),
        education: (candidate.education ?? []).map(mapPublicEducation),
        certifications: (candidate.certifications ?? []).map(mapPublicCertification),
      },
    },
  }
}
